import { ORDER_STATUS_MAPPING, PRODUCT_ID_DATA_ONLY } from '@constants/constants';
import { OrderStatus, ResultsType, ToplineResult } from '@customTypes/order';
import {
  BaseRequisition,
  BaseRequisitionOrder,
  CaseRequisition,
  ClinicianReviewStatus,
  Requisition,
} from '@customTypes/requisition';
import {
  getTimelineStepsFromStatus,
  getReportedStatusLabel,
  findLastStepWithProgress,
} from '@pages/patientDetail/components/PatientDetailOrders/PatientDetailOrderStatusTimeline/utils/orderStatusTimeline';

import { getResultType, isOrderReported } from './order';
import { isSecondaryFindings, isRareDiseaseProduct, isProactiveProduct } from './product';

/**
 * Reported orders that produce a downloadable report, excluding data-only products.
 */
export const getRequisitionReportableOrders = <TOrder extends BaseRequisitionOrder>(
  requisition: BaseRequisition<TOrder>,
): TOrder[] => requisition.orders.filter(order => isOrderReported(order) && order.product_id !== PRODUCT_ID_DATA_ONLY);

export const hasRequisitionDownloads = (requisition: BaseRequisition): boolean =>
  !!requisition.clinical_summary_letter || getRequisitionReportableOrders(requisition).length > 0;

export const getRequisitionName = (requisition: Requisition | CaseRequisition): string => {
  return requisition.name.middle
    ? `${requisition.name.first} ${requisition.name.middle} ${requisition.name.last}`
    : `${requisition.name.first} ${requisition.name.last}`;
};

/**
 * Whether the CLINICIAN_REVIEW step should be skipped for this requisition. True when:
 *   - the backend has marked the requisition as INELIGIBLE for clinician review
 *     (no clinical summary letter will be produced), or
 *   - the field is missing / null (CaseRequisition payloads, legacy responses) — we
 *     have no positive signal that a clinical summary letter is coming, so we omit the
 *     step rather than show it as "in progress" indefinitely.
 *
 * Consumers use this in two ways:
 *   1. To filter the CLINICIAN_REVIEW step out of the timeline (createBaseTimelineSteps).
 *   2. To remap an external status of CLINICIAN_REVIEW to COMPLETED so the final
 *      "Complete" timeline step lights up (getTimelineStepsFromStatus).
 */
export const isClinicianReviewSkipped = (requisition?: BaseRequisition | CaseRequisition | null): boolean => {
  if (!requisition || !('clinician_review_status' in requisition)) {
    return true;
  }
  const status = requisition.clinician_review_status;
  return status == null || status === ClinicianReviewStatus.INELIGIBLE;
};

/**
 * Maps external_status to timeline step title for display
 * This matches the timeline component's status labels by using the same logic
 * @param requisition - The requisition to get the status label for
 * @returns The timeline step title, or fallback label for CANCELED/DENIED, or null for FAILED/undefined
 */
export function getTimelineStatusLabel(requisition?: BaseRequisition | CaseRequisition | null): string | null {
  const externalStatus = requisition?.external_status;

  if (!externalStatus) {
    return null;
  }

  // Handle statuses that aren't part of the timeline flow
  // These use ORDER_STATUS_MAPPING for their labels
  if (externalStatus === OrderStatus.CANCELED || externalStatus === OrderStatus.DENIED) {
    return ORDER_STATUS_MAPPING[externalStatus]?.label ?? null;
  }

  // FAILED status is not displayed
  if (externalStatus === OrderStatus.FAILED) {
    return null;
  }

  // Special handling for REPORTED status - check order completion
  if (externalStatus === OrderStatus.REPORTED) {
    return getReportedStatusLabel(requisition);
  }

  // For all other statuses, use getTimelineStepsFromStatus to get the timeline steps
  // Then find the last step that has progress (complete or in-progress)
  const steps = getTimelineStepsFromStatus(requisition);
  return findLastStepWithProgress(steps);
}

export const getRequisitionResult = (requisition?: BaseRequisition | null): ResultsType => {
  let results: ToplineResult[] = [];

  (requisition?.orders || []).forEach(order => {
    if (order.results?.length) {
      results = results.concat(order.results);
    }
  });

  return getResultType(results);
};

/**
 * Checks if a requisition is a family member (not the primary/proband)
 * @param requisition - The requisition to check
 * @returns true if any related requisition is marked as primary, indicating this is a family member
 */
export const isFamilyMemberRequisition = (requisition: Requisition): boolean => {
  if (!requisition.case_requisitions?.length) return false;
  // If any related requisition is marked as primary, then THIS one is a family member
  return requisition.case_requisitions.some(r => r.is_primary_requisition === true);
};

/**
 * Checks if a requisition has Secondary Findings orders
 * @param requisition - The requisition to check
 * @returns true if any order in the requisition is a Secondary Findings product
 */
export const hasSecondaryFindingsOnRequisition = (requisition: Requisition): boolean => {
  return requisition.orders.some(isSecondaryFindings);
};

/**
 * Checks if a requisition is a family member without Secondary Findings
 * Used to determine if "Completed" label should be shown instead of "Reported"
 * @param requisition - The requisition to check
 * @returns true if requisition is a family member and has no Secondary Findings orders
 */
export const isFamilyMemberWithoutSecondaryFindings = (requisition: Requisition): boolean => {
  return isFamilyMemberRequisition(requisition) && !hasSecondaryFindingsOnRequisition(requisition);
};

/**
 * Checks if a requisition is a rare disease order
 * @param requisition - The requisition to check
 * @returns true if any order in the requisition is a rare disease product
 */
export const isRareDiseaseOrder = (requisition: Requisition): boolean => {
  return requisition.orders.some(isRareDiseaseProduct);
};

/**
 * Checks if a requisition is a proactive order
 * @param requisition - The requisition to check
 * @returns true if any order in the requisition is a proactive product
 */
export const isProactiveOrder = (requisition: Requisition): boolean => {
  return requisition.orders.some(isProactiveProduct);
};
